import {
  TilsynserklaeringFormValues,
  TilsynserklaeringMetadata,
} from "../../interfaces/tilsynserklaering";

const getFromOption = (
  options: { label: string; value: string }[],
  value: string,
) => options.find((option) => option.value === value)?.label ?? "-";

export type TETexts = ReturnType<typeof getTexts>;

const generateCaseTypeQuestion = (type: string, year: string) => ({
  id: `antalSagstyperHvidvaskloven-${type}`,
  label: `Hvor mange sager omfattet af sagskategorien har du samlet set påtaget dig i ${year}? Angiv estimeret antal efter bedste evne og overbevisning.`,
  getAnswerToQuestion: (value: string) => value,
});

export const getTexts = (
  _values: TilsynserklaeringFormValues,
  metadata: TilsynserklaeringMetadata,
) => {
  const { person, erklaeringAar } = metadata;
  const declarationYear = erklaeringAar;
  const jaNejOptions = [
    {
      label: "Ja",
      value: "ja",
    },
    {
      label: "Nej",
      value: "nej",
    },
  ];
  const sagsOptions = [
    {
      label: "Nej",
      value: "nej",
    },
    {
      label: "Ja, 1-5 sager",
      value: "ja-1-5",
    },
    {
      label: "Ja, 6-20 sager",
      value: "ja-6-20",
    },
    {
      label: "Ja, flere end 20 sager",
      value: "ja-flereEnd20",
    },
  ];
  const antalForbrugersagerOptions = [
    {
      label: "1-9 sager",
      value: "1-9-sager",
    },
    {
      label: "10 eller flere sager",
      value: "10-eller-flere-sager",
    },
  ];
  const texts = {
    formTitle: `Erklæring til brug for Advokatsamfundets tilsyn for 
    ${person ? person.navn : ""}`,
    section1: {
      title: "1. Forsikring og garanti",
      section1_1: {
        title: "1.1 Forsikringsforhold",
        question: {
          id: "forsikringSidstAar",
          label: `Var du omfattet af en ansvarsforsikring og garanti, jf. Vedtægt for Det Danske Advokatsamfund §§ 46 og 47 i ${declarationYear}?`,
          options: [
            {
              label: "Ja, hele året",
              value: "ja",
            },
            {
              label: "Ja, siden beskikkelsens udlevering",
              value: "ja_siden_beskikkelsens_udlevering",
            },
            {
              label: "Nej",
              value: "nej",
            },
          ],
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section1.section1_1.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
      section1_2: {
        title: "1.2 Nuværende forsikringsforhold",
        question: {
          id: "nuvaerendeForsikringsselskab",
          label: "I hvilket selskab er din nuværende forsikring tegnet?",
        },
        question2: {
          id: "navnAndetForsikringsselskab",
          label: "Angiv navnet på dit nuværende forsikringsselskab",
        },
        question3: {
          id: "nuvaerendeForsikringPolicenr",
          label: "Hvad er policenummeret på din nuværende forsikring?",
        },
      },
    },
    section2: {
      title: "2. Ejerandele i anden virksomhed",
      section2_1: {
        title: "2.1",
        question: {
          id: "ejerandeleIAndenVirksomhed",
          label:
            "Havde du på et tidspunkt i løbet af erklæringsåret 2025 direkte eller gennem et selskab ejerandele i anden virksomhed end advokatvirksomheden, så du havde indflydelse på driften af den pågældende virksomhed?",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section2.section2_1.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
      section2_2: {
        id: "tilsynEjerandele",
        title: "2.2 Oplysninger om ejerandele",
      },
    },
    section3: {
      title: "3. Forbruger- og Straffesager",
      section3_1: {
        title: "3.1 Forbrugersager",
        question: {
          id: "harDuHaftForbrugersager",
          label: `Har du i ${declarationYear} påtaget dig sager, hvor din klient var forbruger?`,
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section3.section3_1.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
      section3_1a: {
        title: "3.1a Antal Forbruger- og Straffesager",
        question: {
          id: "harDuHaftForbrugersagerUddyb",
          label: `Hvor mange sager har du samlet set påtaget dig i ${declarationYear}, hvor din klient var forbruger? Angiv estimeret antal efter bedste evne og overbevisning.`,
          options: antalForbrugersagerOptions,
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section3.section3_1a.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
      section3_2: {
        title: "3.2 Straffesager",
        question: {
          id: "harDuHaftStraffesager",
          label: `Har du i ${declarationYear} som forsvarer påtaget dig straffesager?`,
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section3.section3_2.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
    },
    section4: {
      title: "4. Hvidvaskloven",
      section4_1: {
        title: "4.1 Hvidvaskloven",
        question: {
          id: "harDuHaftHvidvasksager",
          label: `Har du i ${declarationYear} haft sager omfattet af hvidvaskloven?`,
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section4.section4_1.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
      },
      section4_2: {
        title: "4.2",
        description:
          "De følgende spørgsmål vedrører advokatfirmaets udarbejdelse af dokumenter i henhold til hvidvaskloven og din behandling af sager, som er omfattet af hvidvaskloven, og som du har påtaget dig i perioden fra den 1. januar til den 31. december sidste år.",
        question: {
          id: "hvilkeSagstyperHvidvaskloven",
          label: `Hvilke sagstyper omfattet af hvidvaskloven har du påtaget dig i ${declarationYear}?`,
          options: [
            {
              label: "Bistand ved køb og salg af fast ejendom",
              value: "sagstype-1a",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-1a",
                declarationYear,
              ),
            },
            {
              label: "Bistand ved køb og salg af virksomheder",
              value: "sagstype-1b",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-1b",
                declarationYear,
              ),
            },
            {
              label:
                "Bistand ved forvaltning af klienters penge, værdipapirer og andre aktiver",
              value: "sagstype-2",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-2",
                declarationYear,
              ),
            },
            {
              label:
                "Bistand ved åbning eller forvaltning af bankkonti eller værdipapirdepoter",
              value: "sagstype-3",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-3",
                declarationYear,
              ),
            },
            {
              label:
                "Bistand ved tilvejebringelse af nødvendig kapital til oprettelse, drift eller ledelse af virksomheder",
              value: "sagstype-4",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-4",
                declarationYear,
              ),
            },
            {
              label:
                "Bistand ved oprettelse, drift eller ledelse af virksomheder, fonde m.v.",
              value: "sagstype-5",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-5",
                declarationYear,
              ),
            },
            {
              label:
                "På en klients vegne og for dennes regning foretaget en finansiel transaktion eller en transaktion vedrørende fast ejendom",
              value: "sagstype-6",
              inputQuestion: generateCaseTypeQuestion(
                "sagstype-6",
                declarationYear,
              ),
            },
          ],
          getAnswerToQuestion: (value: string) => {
            return (
              texts.section4.section4_2.question.options.find(
                (option) => option.value === value,
              )?.label ?? "-"
            );
          },
        },
        question2: {
          id: "antalSagstyperHvidvasklovenFastEjendom",
          label: `Hvor mange sager omfattet af sagskategorien fast ejendom har du samlet set påtaget dig i ${declarationYear}? Angiv estimeret antal efter bedste evne og overbevisning.`,
          getAnswerToQuestion: (value: string) => value,
        },
        question3: {
          id: "antalSagstyperHvidvasklovenVirksomheder",
          label: `Hvor mange sager omfattet af sagskategorien virksomheder har du samlet set påtaget dig i ${declarationYear}? Angiv estimeret antal efter bedste evne og overbevisning.`,
          getAnswerToQuestion: (value: string) => value,
        },
      },
      section4_3: {
        title: "4.3",
        question: {
          label: `Hvor mange sager omfattet af hvidvaskloven har du samlet set påtaget dig i ${declarationYear}? Angiv estimeret antal efter bedste evne og overbevisning.`,
          id: "hvorMangeSagerOmfattetAfHvidvaskloven",
          options: [
            {
              value: "1-9",
              label: "1-9 sager",
            },
            {
              value: "10-29",
              label: "10-29 sager",
            },
            {
              value: "30ellerFlere",
              label: "30 eller flere sager",
            },
          ],
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_3.question.options, value),
        },
      },
      section4_4: {
        title: "4.4",
        question: {
          id: "sagerKlientPolitiskEksponeret",
          label: `Har du i ${declarationYear} påtaget dig sager omfattet af hvidvaskloven, hvor klienten var en politisk eksponeret person (PEP) eller nærtstående eller nær samarbejdspartner til en politisk eksponeret person?`,
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_4.question.options, value),
        },
      },
      section4_5: {
        title: "4.5",
        question: {
          label: `Har du i ${declarationYear} påtaget dig sager, der i henhold til hvidvaskloven var kompleks, usædvanlig store, foretaget i et usædvanlig mønster eller ikke havde et åbenbart økonomisk eller lovligt formål?`,
          id: "sagerKompleksEllerUsaedvanlig",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_5.question.options, value),
        },
      },
      section4_6: {
        title: "4.6",
        question: {
          label: `Har du i ${declarationYear} påtaget dig sager, hvor der blev gennemført skærpede kundekendskabsprocedurer?`,
          id: "sagerSkaerpedeKundekendskabsprocedurer",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_6.question.options, value),
        },
      },
      section4_7: {
        title: "4.7",
        question: {
          label: `Har du i ${declarationYear} påtaget dig sager, hvor der blev gennemført lempede kundekendskabsprocedurer?`,
          id: "sagerLempedeKundekendskabsprocedurer",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_7.question.options, value),
        },
      },
      section4_8: {
        title: "4.8",
        question: {
          label: `Har du i ${declarationYear} påtaget dig sager omfattet af hvidvaskloven, hvor klienten var bosat uden for EU eller EØS-lande?`,
          id: "sagerUdenforEU",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_8.question.options, value),
        },
      },
      section4_9: {
        title: "4.9",
        question: {
          id: "sagerTransaktionHoejrisikotredjelande",
          label: `Har du i ${declarationYear} påtaget dig sager omfattet af hvidvaskloven, hvor transaktionen involverede lande, der var opført på EU's liste over højrisikotredjelande?`,
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_9.question.options, value),
        },
      },
      section4_10: {
        title: "4.10",
        question: {
          label: `Har du i ${declarationYear} påtaget dig sager, hvor lande, personer, grupper, juridiske enheder eller organer i sagskomplekset var pålagt en finansiel sanktion?`,
          id: "sagerPaalagtFinansielSanktion",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_10.question.options, value),
        },
      },
      section4_11: {
        title: "4.11",
        question: {
          label: `Har du i ${declarationYear} benyttet tredjemand eller outsourcing i forbindelse med udførelse af kundekendskabsprocedurer?`,
          id: "harDuBenyttetTredjemandEllerOutsourcingKundekendskabsprocedurer",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_11.question.options, value),
        },
      },
      section4_12: {
        title: "4.12",
        question: {
          label: `Har du i ${declarationYear} underrettet Hvidvasksekretariatet om mistanke om hvidvask?`,
          id: "harDuUnderrettetMistankeHvidvask",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_12.question.options, value),
        },
      },
      section4_13: {
        title: "4.13",
        question: {
          label: `Har du i ${declarationYear} underrettet Hvidvasksekretariatet om mistanke om finansiering af terrorisme?`,
          id: "harDuUnderrettetMistankeFinansieringTerrorisme",
          options: sagsOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_13.question.options, value),
        },
      },
      section4_14: {
        title: "4.14",
        question: {
          label:
            "Har den virksomhed, som du er beskæftiget i i dag, udarbejdet en risikovurdering for advokatvirksomheden?",
          id: "virksomhedRisikovurderingAdvokatvirksomhed",
          options: [
            {
              label: "Ja",
              value: "ja",
            },
            {
              label: "Nej",
              value: "nej",
            },
            {
              label: "Jeg er ikke længere beskæftiget i en advokatvirksomhed",
              value: "ikkeBeskaeftigetAdvokatvirksomhed",
            },
          ],
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_14.question.options, value),
        },
      },
      section4_14a: {
        title: "4.14a",
        question: {
          label:
            "Er risikovurderingen foretaget med udgangspunkt i virksomhedens forretningsmodel, og er der identificeret og vurderet risikofaktorer for hver af de sagstyper advokatvirksomheden påtager sig?",
          id: "risikovurderingVirksomhedensForretningsmodel",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_14a.question.options, value),
        },
      },
      section4_15: {
        title: "4.15",
        question: {
          label:
            "Har den advokatvirksomhed, som du er beskæftiget i i dag, udarbejdet mindst én politik i henhold til hvidvaskloven?",
          id: "advokatvirksomhedPolitikHvidvaskloven",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_15.question.options, value),
        },
      },
      section4_15a: {
        title: "4.15a",
        question: {
          label:
            "Har virksomheden i politikken taget stilling til, hvilken forretningsmodel der er valgt og afgrænset hvilke konkrete sagsområder/typer efter hvidvaskloven, som virksomheden ønsker at påtage sig?",
          id: "virksomhedenForretningsmodelSagsomraaderPaatageSig",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_15a.question.options, value),
        },
      },
      section4_16: {
        title: "4.16",
        question: {
          label:
            "Har den advokatvirksomhed, som du er beskæftiget i i dag, udarbejdet forretningsgange i henhold til hvidvaskloven?",
          id: "advokatvirksomhedForretningsgangeHvidvaskloven",
          options: jaNejOptions,
          getAnswerToQuestion: (value: string) =>
            getFromOption(texts.section4.section4_16.question.options, value),
        },
      },
    },
  };
  return texts;
};
